// Singleton popover for numeric parameter editing.
// Linear mode: absolute range slider for addSlider rows.
// Relative mode: log-scale center-returning slider for value-node operand2.

const REL_DIGITS = 2;

export function formatSig4(v: number): string {
  if (!Number.isFinite(v)) return "0";
  if (v === 0) return "0";
  return String(parseFloat(v.toPrecision(4)));
}

/**
 * Maps between a slider's stored value (range geometry) and the number the user
 * reads and types. Only sliders whose stored value differs from their display
 * value need one (e.g. the log-scaled rate slider stores a 0..N index but shows
 * a rate). Identity when absent.
 */
export interface SliderTransform {
  toDisplay: (raw: number) => number;
  fromDisplay: (display: number) => number;
}

interface LinearState {
  kind: "linear";
  hiddenInput: HTMLInputElement;
  format: (v: number) => string;
  onChange: (v: number) => void;
  onCommit?: (v: number) => void;
  outSpan: HTMLElement;
  transform?: SliderTransform;
  allowOutOfRange?: boolean;
}

function toDisplay(s: LinearState, raw: number): number {
  return s.transform ? s.transform.toDisplay(raw) : raw;
}

interface RelativeState {
  kind: "relative";
  center: number;
  onLiveChange: (v: number) => void;
  onCommit: (v: number) => void;
  outSpan: HTMLElement;
}

type PopState = LinearState | RelativeState;

let _state: PopState | null = null;
let _el: HTMLDivElement | null = null;
let _num: HTMLInputElement | null = null;
let _range: HTMLInputElement | null = null;

function roundSig4(v: number): number {
  return Number.isFinite(v) ? parseFloat(v.toPrecision(4)) : 0;
}

function initDom(): void {
  if (_el !== null) return;
  _el = document.createElement("div");
  _el.className = "num-popover";
  _el.style.position = "fixed";
  _el.style.display = "none";
  const topRow = document.createElement("div");
  topRow.className = "num-popover-toprow";
  _num = document.createElement("input");
  _num.type = "number";
  _num.step = "any";
  _num.className = "num-input num-input--popover";
  topRow.append(_num);
  _range = document.createElement("input");
  _range.type = "range";
  _range.className = "num-popover-range";
  _el.append(topRow, _range);
  document.body.appendChild(_el);

  _range.addEventListener("input", onRangeInput);
  _range.addEventListener("pointerup", onRangePointerUp);
  _num.addEventListener("change", onNumChange);
  _num.addEventListener("keydown", (ev: KeyboardEvent): void => {
    if (ev.key === "Enter") (_num as HTMLInputElement).blur();
  });

  document.addEventListener(
    "pointerdown",
    (ev: PointerEvent): void => {
      if (!_state) return;
      const t = ev.target as Node;
      if (_el!.contains(t)) return;
      if (_state.outSpan.contains(t) || t === _state.outSpan) return;
      onNumChange();
      closePopover();
    },
    true,
  );

  document.addEventListener("keydown", (ev: KeyboardEvent): void => {
    if (ev.key === "Escape" && _state) closePopover();
  });
}

/**
 * Relative mode maps the log slider around the center value so moving right
 * always increases the value, whatever the sign. The multiplier can never
 * reach 0, so the end pointing toward 0 (left for a positive center, right for
 * a negative one) snaps to exactly 0; releasing there recenters on 0, from
 * which either sign can be re-selected by dragging.
 */
function onRangeInput(): void {
  if (!_state) return;
  const s = parseFloat(_range!.value);
  if (_state.kind === "linear") {
    _num!.value = String(toDisplay(_state, s));
    _state.hiddenInput.value = String(s);
    _state.outSpan.textContent = _state.format(s);
    _state.onChange(s);
  } else {
    const c = _state.center;
    const v = roundSig4(relativeFromSlider(c, s));
    const display = formatSig4(v);
    _num!.value = display;
    _state.outSpan.textContent = display;
    _state.onLiveChange(v);
  }
}

function relativeFromSlider(center: number, s: number): number {
  if (center > 0) return s <= -1 ? 0 : center * Math.pow(10, s * REL_DIGITS);
  if (center < 0) return s >= 1 ? 0 : center * Math.pow(10, -s * REL_DIGITS);
  return Math.sign(s) * (Math.pow(10, Math.abs(s) * REL_DIGITS) - 1);
}

function onRangePointerUp(): void {
  const s = _state;
  if (!s) return;
  if (s.kind === "relative") {
    const v = parseFloat(_num!.value);
    if (Number.isFinite(v)) {
      applyRelativeValue(s, v);
    } else {
      _range!.value = "0";
    }
    return;
  }
  if (s.onCommit) {
    s.onCommit(parseFloat(s.hiddenInput.value));
  }
}

function applyLinearValue(s: LinearState, raw: number): void {
  const v = s.allowOutOfRange ? raw : Math.min(parseFloat(s.hiddenInput.max), Math.max(parseFloat(s.hiddenInput.min), raw));
  _num!.value = String(toDisplay(s, v));
  _range!.value = String(v);
  s.hiddenInput.value = String(v);
  s.outSpan.textContent = s.format(v);
  s.onChange(v);
  s.onCommit?.(v);
}

function applyRelativeValue(s: RelativeState, raw: number): void {
  const v = roundSig4(raw);
  s.center = v;
  const display = formatSig4(v);
  _num!.value = display;
  s.outSpan.textContent = display;
  s.onCommit(v);
  _range!.value = "0";
}

function onNumChange(): void {
  if (!_state) return;
  const v = parseFloat(_num!.value);
  if (_state.kind === "linear") {
    if (!Number.isFinite(v)) {
      _num!.value = String(toDisplay(_state, parseFloat(_state.hiddenInput.value)));
      return;
    }
    applyLinearValue(_state, _state.transform ? _state.transform.fromDisplay(v) : v);
  } else {
    if (!Number.isFinite(v)) {
      _num!.value = formatSig4(_state.center);
      return;
    }
    applyRelativeValue(_state, v);
  }
}

/**
 * Anchors the popover to an element, opening upward when the anchor is in the
 * lower half of the viewport (e.g. the selection panel dropped to bottom-right)
 * or when a downward popover would overflow — so it never spills off-screen or
 * covers the rows beneath it. Horizontal position is clamped into the viewport.
 */
function positionNear(anchor: HTMLElement): void {
  const pop = _el!;
  pop.style.visibility = "hidden";
  pop.style.display = "flex";
  const pRect = pop.getBoundingClientRect();
  const aRect = anchor.getBoundingClientRect();
  const m = 4;
  let top = aRect.bottom + 4;
  let left = aRect.left;
  if (left + pRect.width > window.innerWidth - m) {
    left = window.innerWidth - m - pRect.width;
  }
  if (left < m) left = m;
  if (aRect.top > window.innerHeight / 2 || top + pRect.height > window.innerHeight - m) {
    top = aRect.top - pRect.height - 4;
  }
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;
  pop.style.visibility = "visible";
}

export function closePopover(): void {
  if (!_state) return;
  _state = null;
  if (_el) _el.style.display = "none";
}

export function openLinearPopover(
  anchor: HTMLElement,
  hiddenInput: HTMLInputElement,
  format: (v: number) => string,
  onChange: (v: number) => void,
  outSpan: HTMLElement,
  onCommit?: (v: number) => void,
  transform?: SliderTransform,
  allowOutOfRange: boolean = false,
): void {
  initDom();
  if (_state?.kind === "linear" && _state.hiddenInput === hiddenInput) {
    closePopover();
    return;
  }
  if (_state) closePopover();
  _state = { kind: "linear", hiddenInput, format, onChange, onCommit, outSpan, transform, allowOutOfRange };
  const v = parseFloat(hiddenInput.value);
  _range!.min = hiddenInput.min;
  _range!.max = hiddenInput.max;
  _range!.step = hiddenInput.dataset.originalStep ?? hiddenInput.step;
  _range!.value = String(v);
  _num!.value = String(toDisplay(_state, v));
  positionNear(anchor);
}

export function openRelativePopover(
  anchor: HTMLElement,
  initialValue: number,
  onLiveChange: (v: number) => void,
  onCommit: (v: number) => void,
  outSpan: HTMLElement,
): void {
  initDom();
  if (_state?.kind === "relative" && _state.outSpan === outSpan) {
    closePopover();
    return;
  }
  if (_state) closePopover();
  _state = { kind: "relative", center: initialValue, onLiveChange, onCommit, outSpan };
  _range!.min = "-1";
  _range!.max = "1";
  _range!.step = "0.005";
  _range!.value = "0";
  _num!.value = formatSig4(initialValue);
  positionNear(anchor);
}

export function syncLinearPopover(hiddenInput: HTMLInputElement, v: number): void {
  if (!_state || _state.kind !== "linear" || _state.hiddenInput !== hiddenInput) return;
  _range!.value = String(v);
  _num!.value = String(toDisplay(_state, v));
}

export function reattachRelativeIfFor(key: string, newOutSpan: HTMLElement, newCenter: number): void {
  if (!_state || _state.kind !== "relative") return;
  if ((_state.outSpan as HTMLElement).dataset.popoverKey !== key) return;
  newOutSpan.dataset.popoverKey = key;
  _state.outSpan = newOutSpan;
  _state.center = newCenter;
  _num!.value = formatSig4(newCenter);
  _range!.value = "0";
}
